import * as React from "react";

import { useKeytipRef } from "@fluentui-contrib/react-keytips";
import { useMergedRefs } from "@fluentui/react-components";
import type {
  KeytipProps,
  ReturnKeytipEventHandler,
} from "@fluentui-contrib/react-keytips";
import type { RefObjectFunction } from "@fluentui/react-hooks";
import { useOnExecuteFromKeytip } from "../useOnExecuteFromKeytip";
import type {
  AppComponentKeytipOptions,
  AppComponentKeytipProps,
  AppComponentWithKeytip,
  DeriveKeytipFunction,
} from "../useAppComponentKeytip";
import { defaultKeytipExecute } from "../keytipExecution";

export const useAppComponentKeytip: <TProps extends AppComponentKeytipProps>(
  props: TProps,
  ref: RefObjectFunction<HTMLElement | null | undefined>,
  componentType: AppComponentWithKeytip,
  deriveKeytip: DeriveKeytipFunction,
  options?: AppComponentKeytipOptions
) => RefObjectFunction<HTMLElement | null | undefined> = (
  props,
  ref,
  type,
  deriveKeytip,
  options
) => {
  const { keytipProps } = options || {};

  const executeFromKeytip = useOnExecuteFromKeytip(props, ref, type, options);

  const keytipExecute = React.useCallback(
    (executeTarget: HTMLElement) => {
      defaultKeytipExecute(executeTarget, executeFromKeytip);
    },
    [executeFromKeytip]
  );
  const keytipRefData = React.useMemo(() => {
    return {
      keytipProps: deriveKeytip(keytipProps as KeytipProps, keytipExecute),
    };
  }, [deriveKeytip, keytipProps, keytipExecute]);

  const localRef =
    keytipRefData.keytipProps &&
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useKeytipRef({
      ...keytipRefData.keytipProps,
      onExecute: (_, { targetElement }: any) => {
        keytipRefData.keytipProps?.onExecute?.(targetElement, targetElement);
      },
      onReturn: keytipRefData.keytipProps
        ?.onReturn as unknown as ReturnKeytipEventHandler,
      truncated: false,
    });
  const mergedRef = useMergedRefs<HTMLElement | null | undefined>(
    localRef as RefObjectFunction<HTMLElement | null | undefined>,
    ref
  );
  keytipProps as KeytipProps;
  return mergedRef;
};
